import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageSquare, Pin, Plus, Send, Shield, Star, Trash2, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import {
  adminDeleteThread,
  adminModerateThread,
  checkAdminStatus,
  createCommunityThread,
  createThreadReply,
  deleteCommunityThread,
  fetchCommunityCategories,
  fetchCommunityThread,
  fetchCommunityThreads,
  fetchThreadReplies,
  toggleThreadPin,
} from "@/lib/api";
import { pickText } from "@/lib/content";

export const CommunityPage = ({ language, vipEmail }) => {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewThread, setShowNewThread] = useState(false);
  const [newThread, setNewThread] = useState({ title: "", content: "", categoryId: "", isCustom: false, customCategory: "" });
  const [isAdmin, setIsAdmin] = useState(false);
  const [newReply, setNewReply] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Handle deep link from notification
  useEffect(() => {
    const threadId = searchParams.get("thread");
    if (threadId && vipEmail && !selectedThread) {
      const loadThreadFromUrl = async () => {
        try {
          const thread = await fetchCommunityThread(threadId, vipEmail);
          await loadReplies(thread);
        } catch (error) {
          console.error("Failed to load thread from URL:", error);
        }
      };
      loadThreadFromUrl();
    }
  }, [searchParams, vipEmail]);

  // Check admin status
  useEffect(() => {
    const checkAdmin = async () => {
      if (!vipEmail) return;
      try {
        const status = await checkAdminStatus(vipEmail);
        setIsAdmin(status.is_admin);
      } catch (error) {
        console.error("Admin check failed:", error);
      }
    };
    checkAdmin();
  }, [vipEmail]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCommunityCategories();
        setCategories(data);
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load categories.", "Impossibile caricare le categorie."));
      }
    };
    loadCategories();
  }, [language]);

  useEffect(() => {
    const loadThreads = async () => {
      if (!vipEmail) return;
      try {
        setLoading(true);
        const data = await fetchCommunityThreads(vipEmail, selectedCategory);
        setThreads(data);
      } catch (error) {
        console.error(error);
        toast.error(pickText(language, "Unable to load discussions.", "Impossibile caricare le discussioni."));
      } finally {
        setLoading(false);
      }
    };
    loadThreads();
  }, [language, vipEmail, selectedCategory]);

  const loadReplies = async (thread) => {
    try {
      setSelectedThread(thread);
      const data = await fetchThreadReplies(thread.id, vipEmail);
      setReplies(data);
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to load replies.", "Impossibile caricare le risposte."));
    }
  };

  const handleCreateThread = async () => {
    if (!newThread.title.trim() || !newThread.content.trim()) return;
    if (!newThread.categoryId && !newThread.isCustom) {
      toast.error(pickText(language, "Please select a category.", "Seleziona una categoria."));
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        email: vipEmail,
        title: newThread.title,
        content: newThread.content,
        category_id: newThread.isCustom ? "custom" : newThread.categoryId,
        is_user_created_category: newThread.isCustom,
        custom_category_name: newThread.isCustom ? newThread.customCategory : undefined,
      };
      const created = await createCommunityThread(payload);
      setThreads((current) => [created, ...current]);
      setNewThread({ title: "", content: "", categoryId: "", isCustom: false, customCategory: "" });
      setShowNewThread(false);
      toast.success(pickText(language, "Discussion created!", "Discussione creata!"));
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to create discussion.", "Impossibile creare la discussione."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateReply = async () => {
    if (!newReply.trim() || !selectedThread) return;

    try {
      setSubmitting(true);
      const payload = { email: vipEmail, content: newReply };
      const created = await createThreadReply(selectedThread.id, payload);
      setReplies((current) => [...current, created]);
      setNewReply("");
      toast.success(pickText(language, "Reply posted!", "Risposta pubblicata!"));
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to post reply.", "Impossibile pubblicare la risposta."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteThread = async (threadId) => {
    try {
      // Use admin delete if admin, otherwise regular delete
      if (isAdmin) {
        await adminDeleteThread(threadId, vipEmail);
      } else {
        await deleteCommunityThread(threadId, vipEmail);
      }
      setThreads((current) => current.filter((t) => t.id !== threadId));
      if (selectedThread?.id === threadId) {
        setSelectedThread(null);
        setReplies([]);
      }
      toast.success(pickText(language, "Discussion deleted.", "Discussione eliminata."));
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || pickText(language, "Unable to delete discussion.", "Impossibile eliminare la discussione."));
    }
  };

  const handleTogglePin = async (threadId, currentPinned) => {
    try {
      // Use admin endpoint if admin, otherwise regular pin toggle
      if (isAdmin) {
        const result = await adminModerateThread(threadId, { email: vipEmail, pinned: !currentPinned });
        setThreads((current) =>
          current.map((t) => (t.id === threadId ? { ...t, pinned: result.pinned } : t))
        );
        if (selectedThread?.id === threadId) {
          setSelectedThread((prev) => ({ ...prev, pinned: result.pinned }));
        }
      } else {
        const updated = await toggleThreadPin(threadId, vipEmail, !currentPinned);
        setThreads((current) =>
          current.map((t) => (t.id === threadId ? { ...t, pinned: updated.pinned } : t))
        );
        if (selectedThread?.id === threadId) {
          setSelectedThread((prev) => ({ ...prev, pinned: updated.pinned }));
        }
      }
      toast.success(
        !currentPinned
          ? pickText(language, "Thread pinned!", "Discussione fissata!")
          : pickText(language, "Thread unpinned.", "Discussione sbloccata.")
      );
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || pickText(language, "Unable to pin thread.", "Impossibile fissare la discussione."));
    }
  };

  const handleToggleFeatured = async (threadId, currentFeatured) => {
    if (!isAdmin) return;
    try {
      const result = await adminModerateThread(threadId, { email: vipEmail, featured: !currentFeatured });
      setThreads((current) =>
        current.map((t) => (t.id === threadId ? { ...t, featured: result.featured } : t))
      );
      if (selectedThread?.id === threadId) {
        setSelectedThread((prev) => ({ ...prev, featured: result.featured }));
      }
      toast.success(
        result.featured
          ? pickText(language, "Thread featured!", "Discussione in evidenza!")
          : pickText(language, "Thread unfeatured.", "Discussione rimossa dall'evidenza.")
      );
    } catch (error) {
      console.error(error);
      toast.error(pickText(language, "Unable to feature thread.", "Impossibile mettere in evidenza."));
    }
  };

  return (
    <div className="space-y-10" data-testid="community-page">
      <div className="max-w-3xl space-y-4">
        <p className="section-label" data-testid="community-eyebrow">
          {pickText(language, "V.I.P. Community", "Comunità V.I.P.")}
        </p>
        <h2 className="text-5xl text-foreground md:text-6xl" data-testid="community-title">
          {pickText(language, "Discussion sanctuary for temple members.", "Santuario di discussione per i membri del tempio.")}
        </h2>
        <p className="body-copy" data-testid="community-description">
          {pickText(language, "Share insights, ask questions, and connect with fellow seekers exploring the Anatomia Occulti.", "Condividi intuizioni, fai domande e connettiti con altri cercatori che esplorano l'Anatomia Occulti.")}
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[280px_1fr]" data-testid="community-layout">
        {/* Categories Sidebar */}
        <div className="space-y-4">
          <div className="glass-card rounded-[2rem] p-5" data-testid="community-categories-panel">
            <div className="flex items-center gap-3 text-primary">
              <Users className="h-5 w-5" />
              <p className="section-label">{pickText(language, "Categories", "Categorie")}</p>
            </div>
            <div className="mt-4 space-y-2">
              <button
                className={`w-full rounded-full border px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  selectedCategory === null ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 text-slate-300 hover:border-primary/30 hover:text-primary"
                }`}
                data-testid="community-category-all"
                onClick={() => setSelectedCategory(null)}
                type="button"
              >
                {pickText(language, "All Discussions", "Tutte le Discussioni")}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`w-full rounded-full border px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                    selectedCategory === cat.id ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 text-slate-300 hover:border-primary/30 hover:text-primary"
                  }`}
                  data-testid={`community-category-${cat.id}`}
                  onClick={() => setSelectedCategory(cat.id)}
                  type="button"
                >
                  {language === "it" ? cat.title_it : cat.title_en}
                </button>
              ))}
            </div>
          </div>

          <Button
            className="ritual-button h-auto w-full rounded-full"
            data-testid="community-new-thread-button"
            onClick={() => setShowNewThread(true)}
            type="button"
          >
            <Plus className="h-4 w-4" />
            {pickText(language, "New Discussion", "Nuova Discussione")}
          </Button>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* New Thread Form */}
          {showNewThread && (
            <div className="glass-card rounded-[2rem] p-6" data-testid="community-new-thread-form">
              <p className="section-label">{pickText(language, "Start a New Discussion", "Inizia una Nuova Discussione")}</p>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.24em] text-slate-400">
                    {pickText(language, "Category", "Categoria")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        className={`rounded-full border px-4 py-2 text-xs transition-colors ${
                          newThread.categoryId === cat.id && !newThread.isCustom
                            ? "border-primary/40 bg-primary/10 text-primary"
                            : "border-white/10 text-slate-300 hover:border-primary/30"
                        }`}
                        onClick={() => setNewThread((c) => ({ ...c, categoryId: cat.id, isCustom: false }))}
                        type="button"
                      >
                        {language === "it" ? cat.title_it : cat.title_en}
                      </button>
                    ))}
                    <button
                      className={`rounded-full border px-4 py-2 text-xs transition-colors ${
                        newThread.isCustom ? "border-primary/40 bg-primary/10 text-primary" : "border-white/10 text-slate-300 hover:border-primary/30"
                      }`}
                      onClick={() => setNewThread((c) => ({ ...c, isCustom: true, categoryId: "" }))}
                      type="button"
                    >
                      {pickText(language, "+ Custom", "+ Personalizzato")}
                    </button>
                  </div>
                  {newThread.isCustom && (
                    <Input
                      className="mt-3 h-11 border-white/10 bg-black/20 text-white"
                      data-testid="community-custom-category-input"
                      onChange={(e) => setNewThread((c) => ({ ...c, customCategory: e.target.value }))}
                      placeholder={pickText(language, "Custom category name...", "Nome categoria personalizzata...")}
                      value={newThread.customCategory}
                    />
                  )}
                </div>
                <Input
                  className="h-11 border-white/10 bg-black/20 text-white"
                  data-testid="community-thread-title-input"
                  onChange={(e) => setNewThread((c) => ({ ...c, title: e.target.value }))}
                  placeholder={pickText(language, "Discussion title...", "Titolo discussione...")}
                  value={newThread.title}
                />
                <Textarea
                  className="min-h-[120px] border-white/10 bg-black/20 text-white"
                  data-testid="community-thread-content-input"
                  onChange={(e) => setNewThread((c) => ({ ...c, content: e.target.value }))}
                  placeholder={pickText(language, "Share your thoughts, questions, or insights...", "Condividi i tuoi pensieri, domande o intuizioni...")}
                  value={newThread.content}
                />
                <div className="flex gap-3">
                  <Button
                    className="ritual-button h-auto rounded-full"
                    data-testid="community-submit-thread-button"
                    disabled={submitting}
                    onClick={handleCreateThread}
                    type="button"
                  >
                    {pickText(language, submitting ? "Creating..." : "Create Discussion", submitting ? "Creazione..." : "Crea Discussione")}
                  </Button>
                  <button
                    className="ghost-button"
                    data-testid="community-cancel-thread-button"
                    onClick={() => setShowNewThread(false)}
                    type="button"
                  >
                    {pickText(language, "Cancel", "Annulla")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Thread List or Thread Detail */}
          {selectedThread ? (
            <div className="glass-card rounded-[2rem] p-6" data-testid="community-thread-detail">
              <button
                className="ghost-button mb-4"
                data-testid="community-back-button"
                onClick={() => { setSelectedThread(null); setReplies([]); }}
                type="button"
              >
                ← {pickText(language, "Back to discussions", "Torna alle discussioni")}
              </button>
              <div className="rounded-[1.5rem] border border-white/10 bg-black/20 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{selectedThread.category_name}</p>
                      {selectedThread.featured && (
                        <span className="flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] text-amber-400">
                          <Star className="h-3 w-3" /> Featured
                        </span>
                      )}
                    </div>
                    <h3 className="mt-2 text-2xl text-foreground" data-testid="community-thread-title">{selectedThread.title}</h3>
                    <p className="mt-1 text-xs text-slate-500">
                      {selectedThread.author_display} · {new Date(selectedThread.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {/* Show controls for author OR admin */}
                  {(selectedThread.author_email === vipEmail || isAdmin) && (
                    <div className="flex gap-2">
                      {/* Admin badge */}
                      {isAdmin && selectedThread.author_email !== vipEmail && (
                        <span className="flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-900/20 px-2 py-1 text-[10px] text-amber-400">
                          <Shield className="h-3 w-3" /> Admin
                        </span>
                      )}
                      {/* Feature button (admin only) */}
                      {isAdmin && (
                        <button
                          className={`ghost-button ${selectedThread.featured ? "text-amber-400" : "text-slate-400"} hover:border-amber-400/30`}
                          data-testid="community-feature-thread-button"
                          onClick={() => handleToggleFeatured(selectedThread.id, selectedThread.featured)}
                          title={selectedThread.featured ? pickText(language, "Unfeature", "Rimuovi evidenza") : pickText(language, "Feature", "Metti in evidenza")}
                          type="button"
                        >
                          <Star className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        className={`ghost-button ${selectedThread.pinned ? "text-primary" : "text-slate-400"} hover:border-primary/30`}
                        data-testid="community-pin-thread-button"
                        onClick={() => handleTogglePin(selectedThread.id, selectedThread.pinned)}
                        title={selectedThread.pinned ? pickText(language, "Unpin", "Sblocca") : pickText(language, "Pin", "Fissa")}
                        type="button"
                      >
                        <Pin className="h-4 w-4" />
                      </button>
                      <button
                        className="ghost-button text-red-200 hover:border-red-400/30"
                        data-testid="community-delete-thread-button"
                        onClick={() => handleDeleteThread(selectedThread.id)}
                        type="button"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-4 text-sm leading-7 text-slate-300" data-testid="community-thread-content">{selectedThread.content}</p>
              </div>

              {/* Replies */}
              <div className="mt-6 space-y-4">
                <p className="section-label">{pickText(language, `${replies.length} Replies`, `${replies.length} Risposte`)}</p>
                {replies.map((reply) => (
                  <div
                    className="rounded-[1.5rem] border border-white/10 bg-black/20 p-4"
                    data-testid={`community-reply-${reply.id}`}
                    key={reply.id}
                  >
                    <p className="text-xs text-slate-500">{reply.author_display} · {new Date(reply.created_at).toLocaleDateString()}</p>
                    <p className="mt-2 text-sm leading-7 text-slate-300">{reply.content}</p>
                  </div>
                ))}

                {/* Reply Form */}
                <div className="flex gap-3">
                  <Input
                    className="h-11 flex-1 border-white/10 bg-black/20 text-white"
                    data-testid="community-reply-input"
                    onChange={(e) => setNewReply(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreateReply()}
                    placeholder={pickText(language, "Write a reply...", "Scrivi una risposta...")}
                    value={newReply}
                  />
                  <Button
                    className="ritual-button h-auto rounded-full"
                    data-testid="community-submit-reply-button"
                    disabled={submitting}
                    onClick={handleCreateReply}
                    type="button"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass-card rounded-[2rem] p-6" data-testid="community-thread-list">
              <div className="flex items-center gap-3 text-primary">
                <MessageSquare className="h-5 w-5" />
                <p className="section-label">
                  {selectedCategory
                    ? categories.find((c) => c.id === selectedCategory)?.[language === "it" ? "title_it" : "title_en"] || "Discussions"
                    : pickText(language, "All Discussions", "Tutte le Discussioni")}
                </p>
              </div>

              {loading ? (
                <div className="mt-4 rounded-3xl border border-white/10 bg-black/20 p-4 text-sm text-slate-300">
                  {pickText(language, "Loading discussions...", "Caricamento discussioni...")}
                </div>
              ) : threads.length === 0 ? (
                <div className="mt-4 rounded-3xl border border-dashed border-white/10 bg-black/20 p-5 text-sm text-slate-400">
                  {pickText(language, "No discussions yet. Start one!", "Nessuna discussione ancora. Iniziane una!")}
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {threads.map((thread) => (
                    <button
                      className={`w-full rounded-[1.5rem] border bg-black/20 p-4 text-left transition-colors hover:border-primary/30 ${
                        thread.pinned ? "border-primary/30" : "border-white/10"
                      }`}
                      data-testid={`community-thread-${thread.id}`}
                      key={thread.id}
                      onClick={() => loadReplies(thread)}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          {thread.pinned && <Pin className="h-3 w-3 text-primary" />}
                          <p className="text-xs uppercase tracking-[0.24em] text-primary/80">{thread.category_name}</p>
                        </div>
                        <p className="text-xs text-slate-500">{thread.reply_count} {pickText(language, "replies", "risposte")}</p>
                      </div>
                      <h4 className="mt-2 text-lg text-foreground">{thread.title}</h4>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-400">{thread.content}</p>
                      <p className="mt-2 text-xs text-slate-500">
                        {thread.author_display} · {new Date(thread.created_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
